import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { Check, MessageCircle } from 'lucide-react';
import { PhoneMock } from '../components/landing/PhoneMock';
import { trackEvent } from '../utils/analytics';
import { rememberSignupIntent, type SignupIntentCta } from '../utils/signupIntent';
import logoIcon from '../assets/logo-icon.png';

/**
 * Marketing front door at `/` for signed-out visitors.
 *
 * Implements the "BT Vision Landing" design. The design carries three headline
 * variants as an A/B prop (`headlineAngle`); this ships its default, "Sem
 * planilha" — the other two are kept below so swapping is a one-line change.
 * The `mostrarTorneios` strip is off by default there and is omitted here.
 */

const SHELL = 'max-w-[1120px] mx-auto px-5 sm:px-6 lg:px-8';

/** Design's A/B angles. Ship one; the others are ready to swap in. */
const HEADLINES = {
  semPlanilha: {
    title: 'Organize rankings de beach tennis sem planilha',
    sub: 'O BT Vision sorteia os grupos, atualiza a classificação a cada placar e cobra a inscrição no PIX direto pra sua conta. Criar o ranking leva um minuto, os integrantes entram usando um link de convite.',
  },
  alivio: {
    title: 'Você marca o jogo. O resto o BT Vision faz.',
    sub: 'Sorteio de duplas sem repetição, tabela que se atualiza sozinha e inscrição cobrada por QR Code PIX. Tudo a partir de um link no grupo do WhatsApp.',
  },
  pix: {
    title: 'Do sorteio ao PIX, o seu Rei/Rainha em um link.',
    sub: 'Crie o ranking em um minuto, mande o link no grupo e deixe o app sortear as rodadas, calcular a tabela e liberar a entrada de quem pagou.',
  },
} as const;

const HEADLINE = HEADLINES.semPlanilha;

const HERO_PROOFS = [
  'Grátis para criar e participar',
  'Sem instalar — abre no navegador',
  'Jogador entra pelo link, só com e-mail',
  'O PIX cai na sua conta, não na nossa',
];

const STEPS = [
  { title: 'Crie o ranking', body: 'Nome, pontuação, quantas rodadas tem a temporada.' },
  { title: 'Convide pelo link', body: 'Um link pronto pra você enviar no grupo do WhatsApp.' },
  { title: 'Sorteie a rodada', body: 'O app monta os grupos e evita repetir as mesmas duplas.' },
  { title: 'Classificação atualizada', body: 'Cada jogador lança o placar. Pontos e posição saem na hora.' },
];

const PROOFS = [
  {
    title: 'Sorteio que não repete as mesmas duplas',
    body: 'Grupos de 4 montados a partir de quem já jogou com quem, para minimizar confrontos repetidos rodada após rodada.',
  },
  {
    title: 'Descartes e coringa, como num ranking de verdade',
    body: 'As piores rodadas saem do total de cada jogador. Faltou gente? Um coringa entra como substituto, com recuperação de pontos se você quiser.',
  },
  {
    title: 'Inscrição sem cobrança manual',
    body: 'QR Code PIX gerado pra cada jogador, com valor e prazo definidos por você. Assim que o pagamento cai, a entrada é liberada — e o dinheiro vai direto para a sua conta.',
  },
  {
    title: 'Só entra no sorteio quem confirmou presença',
    body: 'Ative a confirmação por rodada e o sorteio considera só quem vai jogar. Sem buraco no grupo, sem W.O. surpresa.',
  },
  {
    title: 'Ninguém perde nenhuma atualização',
    body: 'Os jogadores recebem aviso por e-mail quando a rodada é sorteada, e quando é finalizada, ficando a par de tudo, sempre.',
  },
  {
    title: 'A pontuação é do seu jeito',
    body: 'Defina as regras: pontos por vitória, derrota, games feitos, saldo de games, penalidade para W.O., etc. Configure uma vez e o app calcula tudo sozinho.',
  },
];

const PLAYER_PROOFS = [
  'Entra pelo link ou código de 8 caracteres',
  'Grátis para o jogador, sempre',
  'Placar contestado? O admin corrige em um toque',
  'Instala na tela inicial e abre offline, se quiser',
];

const OBJECTIONS = [
  { q: 'Quanto custa?', a: 'Grátis para criar e participar.' },
  { q: 'Preciso instalar?', a: 'Não. Abre no navegador. Instalar é opcional.' },
  { q: 'E quem não tem conta?', a: 'Entra pelo link, só com e-mail ou conta Google.' },
  { q: 'O dinheiro passa por vocês?', a: 'Não. Vai direto para a sua conta Asaas, nossa I.P. parceira.' },
];

const CTA_CLASS =
  'inline-flex items-center justify-center rounded-xl font-bold text-deep-blue gradient-sunset transition-[filter] hover:brightness-110';

/** Business WhatsApp: international format, digits only (55 + area + number). */
const WHATSAPP_NUMBER = '5516993143383';

const CONTACT_MESSAGE =
  'Oi! Vi o BT Vision e queria tirar uma dúvida / ver funcionando antes de criar meu ranking.';

export function LandingPage() {
  const navigate = useNavigate();

  /**
   * Park which CTA was clicked before sending them to sign up. AuthContext
   * attaches it to the account once one exists, so first contact after signup
   * can reference why they came.
   */
  const startSignup = (cta: SignupIntentCta) => {
    rememberSignupIntent(cta);
    trackEvent('btv_landing_cta_click', { cta });
    navigate('/login');
  };

  const goToLogin = () => navigate('/login');

  return (
    <div className="min-h-screen bg-deep-blue text-white max-md:pb-[92px]">
      {/* ── Nav ─────────────────────────────────────────────── */}
      <nav
        className={
          'max-md:sticky max-md:top-0 max-md:z-30 max-md:bg-deep-blue/90 max-md:backdrop-blur-md ' +
          `max-md:border-b max-md:border-white/[.06] ${SHELL} py-5 ` +
          'flex items-center justify-between gap-3 flex-wrap'
        }
      >
        <div className="flex items-center gap-3">
          <img src={logoIcon} alt="" className="w-[38px] h-[38px] rounded-[9px] object-contain" />
          <span className="text-[19px] font-bold tracking-[-0.01em]">BT Vision</span>
        </div>
        <div className="flex items-center gap-3 sm:gap-6">
          <a
            href="#como-funciona"
            className="hidden sm:inline text-[15px] text-white/70 hover:text-white transition-colors"
          >
            Como funciona
          </a>
          <button
            onClick={goToLogin}
            className="text-[15px] font-semibold text-white/70 px-4 py-2.5 border border-white/20 rounded-[10px] hover:text-white hover:border-white/40 transition-colors"
          >
            Entrar
          </button>
          <button
            onClick={() => startSignup('nav')}
            className={`${CTA_CLASS} hidden md:inline-flex text-[15px] px-5 py-2.5`}
          >
            Criar meu ranking
          </button>
        </div>
      </nav>

      {/* ── Hero ────────────────────────────────────────────── */}
      <header className={`${SHELL} pt-12 pb-16 lg:pb-[88px] grid gap-10 lg:gap-14 lg:grid-cols-2 items-center`}>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <span className="inline-flex items-center gap-2 bg-turquoise/[.12] border border-turquoise/35 text-turquoise text-[13px] font-semibold px-3.5 py-1.5 rounded-full mb-6 tracking-[.02em]">
            Para quem organiza rankings estilo Rei/Rainha
          </span>
          <h1 className="font-display font-semibold text-[clamp(34px,5.5vw,52px)] leading-[1.08] tracking-[-0.025em] mb-5 text-pretty">
            {HEADLINE.title}
          </h1>
          <p className="text-[19px] leading-[1.55] text-white/70 mb-8 max-w-[520px] text-pretty">
            {HEADLINE.sub}
          </p>

          {/* Hidden on phones: the fixed bottom bar already carries this CTA,
              and the two render stacked on top of each other. */}
          <button
            onClick={() => startSignup('hero')}
            className={`${CTA_CLASS} max-md:hidden text-[17px] px-8 py-4 mb-5`}
            style={{ boxShadow: '0 8px 32px rgba(255,107,107,.25)' }}
          >
            Criar meu ranking grátis
          </button>

          <ul className="flex flex-wrap gap-x-6 gap-y-2.5 max-w-[540px] list-none p-0 m-0">
            {HERO_PROOFS.map((proof) => (
              <li key={proof} className="flex items-center gap-[7px] text-sm text-white/50">
                <Check className="w-4 h-4 text-success-green shrink-0" strokeWidth={3} />
                {proof}
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex justify-center items-center"
        >
          <PhoneMock />
        </motion.div>
      </header>

      {/* ── Como funciona ───────────────────────────────────── */}
      <section id="como-funciona" className="border-t border-white/[.08] bg-white/[.02]">
        <div className={`${SHELL} py-20`}>
          <h2 className="font-display font-semibold text-[clamp(27px,4.5vw,34px)] tracking-[-0.02em] mb-3 text-pretty">
            Quatro passos. O primeiro leva um minuto.
          </h2>
          <p className="text-[17px] text-white/50 mb-12">Do zero à primeira rodada sorteada, sem planilha.</p>
          <ol className="grid gap-8 sm:gap-x-6 sm:grid-cols-2 lg:grid-cols-4 list-none p-0 m-0">
            {STEPS.map((step, i) => (
              <li key={step.title} className="flex flex-col gap-3">
                <span className="font-display font-bold text-[44px] leading-none gradient-text-sunset">{i + 1}</span>
                <h3 className="font-display text-[19px] font-bold">{step.title}</h3>
                <p className="text-[15px] leading-[1.55] text-white/70">{step.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ── Provas ──────────────────────────────────────────── */}
      <section className={`${SHELL} py-[88px]`}>
        <h2 className="font-display font-semibold text-[clamp(27px,4.5vw,34px)] tracking-[-0.02em] mb-12 max-w-[640px] text-pretty">
          Feito por quem sabe o trabalho que dá organizar um ranking.
        </h2>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {PROOFS.map((item) => (
            <div key={item.title} className="bg-white/5 border border-white/[.08] rounded-2xl p-7">
              <h3 className="font-display text-xl font-bold text-turquoise mb-2.5">{item.title}</h3>
              <p className="text-[15px] leading-[1.6] text-white/70">{item.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Jogador ─────────────────────────────────────────── */}
      <section className="bg-sand">
        <div className={`${SHELL} py-20 grid gap-12 lg:grid-cols-2 items-center`}>
          <div>
            <h2 className="font-display font-semibold text-[clamp(27px,4.5vw,34px)] tracking-[-0.02em] mb-4 text-deep-blue text-pretty">
              E pra quem só quer jogar?
              <br />
              Entra pelo link.
            </h2>
            <p className="text-[17px] leading-[1.6] text-deep-blue/75">
              Seu grupo não precisa baixar app nem criar conta complicada. Entra pelo link, só com
              e-mail ou Google, lança o placar e vê em que posição ficou. Sem assinatura, sem cartão,
              sem obrigação de conectar nada.
            </p>
          </div>
          <ul className="flex flex-col gap-3.5 list-none p-0 m-0">
            {PLAYER_PROOFS.map((proof) => (
              <li
                key={proof}
                className="flex items-center gap-3 bg-deep-blue/[.06] rounded-xl px-5 py-4"
              >
                <Check className="w-4 h-4 text-deep-blue shrink-0" strokeWidth={3} />
                <span className="text-[15px] text-deep-blue/85">{proof}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── Objeções ────────────────────────────────────────── */}
      <section className={`${SHELL} pt-[88px] pb-16`}>
        <dl className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4 m-0">
          {OBJECTIONS.map((item) => (
            <div key={item.q}>
              <dt className="font-display text-base font-bold text-white/50 mb-2">{item.q}</dt>
              <dd className="text-[15px] leading-[1.55] text-white m-0">{item.a}</dd>
            </div>
          ))}
        </dl>
      </section>

      {/* ── CTA final ───────────────────────────────────────── */}
      <section id="criar" className={`${SHELL} pt-8 pb-[104px] text-center`}>
        <img
          src={logoIcon}
          alt=""
          className="w-14 h-14 rounded-[13px] mb-6 mx-auto object-contain"
        />
        <h2 className="font-display font-semibold text-[clamp(30px,5vw,40px)] tracking-[-0.02em] mb-3.5 text-pretty">
          Aposente a planilha hoje.
        </h2>
        <p className="text-[17px] text-white/70 mb-8">
          Crie o ranking em um minuto e mande o link no grupo ainda hoje.
        </p>
        <button
          onClick={() => startSignup('final')}
          className={`${CTA_CLASS} text-[17px] px-9 py-4 mb-[18px]`}
          style={{ boxShadow: '0 8px 32px rgba(255,107,107,.25)' }}
        >
          Criar meu ranking grátis
        </button>
        <div className="flex justify-center gap-x-6 gap-y-2 flex-wrap text-[13px] text-white/50">
          <span>✓ Grátis</span>
          <span>✓ Sem instalar</span>
          <span>✓ Sem cartão</span>
          <span>✓ PIX na sua conta</span>
        </div>

        <div className="mt-14 pt-10 border-t border-white/[.08] max-w-[560px] mx-auto">
          <h3 className="font-display text-lg font-bold mb-1.5">
            Ficou com dúvida? Quer ver funcionando antes?
          </h3>
          <p className="text-[15px] text-white/60 mb-6">
            Me chama e eu te mostro — ou monto o seu primeiro ranking junto com você.
          </p>
          {/* WhatsApp is the one channel this audience actually answers on. */}
          <div className="flex justify-center">
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(CONTACT_MESSAGE)}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent('btv_landing_contact_click', { channel: 'whatsapp' })}
              className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-xl text-[16px] font-semibold text-white bg-white/10 border border-white/20 hover:bg-white/[.16] hover:border-white/30 transition-colors"
            >
              <MessageCircle className="w-5 h-5 text-success-green" />
              Falar no WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* ── Barra fixa (mobile) ─────────────────────────────── */}
      {/* The CTA stays reachable at any scroll position on a phone, where the
          nav's own button is hidden. Padding carries the home-indicator inset
          so the button never sits under it. */}
      <div
        className="md:hidden fixed bottom-0 left-0 right-0 z-30 px-4 pt-3 bg-deep-blue/90 backdrop-blur-md border-t border-white/[.08]"
        style={{ paddingBottom: 'calc(12px + env(safe-area-inset-bottom))' }}
      >
        <button
          onClick={() => startSignup('sticky')}
          className={`${CTA_CLASS} w-full text-[16px] py-3.5`}
        >
          Criar meu ranking grátis
        </button>
      </div>

      {/* ── Rodapé ──────────────────────────────────────────── */}
      <footer className="border-t border-white/[.08]">
        <div className={`${SHELL} py-7 flex items-center justify-between flex-wrap gap-4`}>
          <div className="flex items-center gap-2.5">
            <img src={logoIcon} alt="" className="w-6 h-6 rounded-md object-contain" />
            <span className="text-sm text-white/50">BT Vision · 100% em português</span>
          </div>
          <div className="flex gap-6">
            <Link to="/terms" className="text-sm text-white/50 hover:text-turquoise transition-colors">
              Termos
            </Link>
            <Link to="/privacy" className="text-sm text-white/50 hover:text-turquoise transition-colors">
              Privacidade
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
